<template>
  <div :id="idTippyParent" ref="tippyParent">
    <component :is="tag" v-if="showDates" data-cy="sidebar-program-dates-label">
      <slot>
        <span
          :id="id"
          ref="datesEl"
          class="inline-flex items-center"
          :class="{ 'flex-row-reverse': reverseIconOrder, [textClass]: true }"
          tabindex="0"
        >
          <span v-if="showIcon" class="material-symbols-outlined mr-2 leading-none" :class="iconClass" aria-hidden="true">{{ iconName }}</span>
          <p>{{ dates }}</p>
        </span>
      </slot>
    </component>
  </div>
</template>

<script>
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";
import "tippy.js/themes/light.css";
export default {
  name: "ProgramDates",
  props: {
    id: {
      type: String,
      default: "anid",
    },
    dates: {
      type: String,
      default: "",
      required: true,
    },
    startIsFinal: {
      type: Boolean,
      default: false,
      required: true,
    },
    endIsFinal: {
      type: Boolean,
      default: false,
      required: true,
    },
    tag: {
      type: String,
      default: "div",
    },
    reverseIconOrder: {
      type: Boolean,
      default: false,
    },
    experienceCardIcon: {
      type: Boolean,
      default: false,
    },
    textClass: {
      type: String,
      default: "",
    },
  },
  computed: {
    showTooltip() {
      if (this.experienceCardIcon && this.datesConfirmed) {
        return false;
      }
      return true;
    },
    showIcon() {
      if (this.experienceCardIcon && this.datesConfirmed) {
        return false;
      }
      return true;
    },
    showDates() {
      return this.dates;
    },
    overlayMessage() {
      let message = "";
      if (this.startIsFinal === true && this.endIsFinal === true) {
        message = "Program dates are confirmed.";
      } else if (this.startIsFinal === true && this.endIsFinal === false) {
        message = "Start date is final but end date will be finalized closer to the start of the program.";
      } else if (this.startIsFinal === false && this.endIsFinal === true) {
        message = "End date is final but start date will be finalized closer to the start of the program.";
      } else {
        message = "Dates will be finalized closer to the start of the program."; // Both false
      }
      return message;
    },
    datesConfirmed() {
      return this.startIsFinal === true && this.endIsFinal === true;
    },
    iconName() {
      if (this.datesConfirmed) {
        return "check_circle";
      }
      return this.experienceCardIcon ? "error" : "warning";
    },
    iconClass() {
      return [
        this.datesConfirmed ? "text-success-900" : this.experienceCardIcon ? "text-warning-800" : "text-warning-900",
        this.experienceCardIcon ? "!text-lg" : "!text-2xl",
        { "ml-2": this.reverseIconOrder },
      ];
    },
    idTippyParent() {
      return `tippy-parent-${this.id}`;
    },
  },
  watch: {
    overlayMessage: "scheduleTooltipSync",
    showTooltip: "scheduleTooltipSync",
    showDates: "scheduleTooltipSync",
  },
  created() {
    this.tippyInstance = null;
  },
  mounted() {
    this.syncTooltip();
  },
  beforeUnmount() {
    this.destroyTooltip();
  },
  methods: {
    scheduleTooltipSync() {
      // Parent element could be hidden/shown in the same tick.
      // Wait for DOM before synching Tippy.
      this.$nextTick(this.syncTooltip);
    },
    destroyTooltip() {
      this.tippyInstance?.destroy();
      this.tippyInstance = null;
    },
    syncTooltip() {
      const reference = this.$refs.datesEl;
      if (!reference || !this.showTooltip) {
        this.destroyTooltip();
        return;
      }
      if (this.tippyInstance?.reference === reference) {
        this.tippyInstance.setContent(this.overlayMessage);
        return;
      }
      this.destroyTooltip();
      this.tippyInstance = tippy(reference, {
        content: this.overlayMessage,
        theme: "light",
        role: "tooltip",
        appendTo: this.$refs.tippyParent,
        aria: {
          content: "describedby",
          expanded: "auto",
        },
      });
    },
  },
};
</script>
